/**
 * The single avatar primitive for every app (BL-1701, ratifying the `Avatar`
 * row of the ADR 030 Canon).
 *
 * The promotion collapses the last hand-rolled person circles onto one
 * structure: admin-frontend's BL-1634 `Avatar` (itself the merge of six
 * customer circles), the settings screens' `.ui-avatar` CSS class and its
 * five staff tiles, the clerk console top bar's 24px chip with its own
 * `initialsOf()`, the profile modal's 80px circle, and bidder-web's three
 * photo-or-initials chips — each of the last three with its own
 * `getUserInitials`. They all drew the same shape: a fixed box, a fill, and
 * ONE of a photo / an icon / two letters. They differed only in VALUES, and in
 * whether they had a photo slot at all.
 *
 * WHY a `dialect` object rather than a pile of `className` props: ADR 030's
 * foundation is shared but its dialects are deliberately different, and
 * `sharing.md` § 1 caps a per-app skin at a thin wrapper. One frozen object of
 * class names per app keeps the appearance a piece of DATA the skin declares
 * once. The core is generic on the skin's `Size` / `Tone` unions on purpose:
 * admin's tones are measured token pairings on paper surfaces, bidder's is a
 * runtime tenant tint, and a shared vocabulary would only invite "unifying"
 * them — which ADR 030 calls a regression.
 *
 * WHAT the core decides, and the skins may not (guarded by
 * `Avatar.skins.guard.test.ts`):
 *   - precedence: a photo wins over an icon child, which wins over initials.
 *     A skin that re-decided this is how bidder-web ended up with three copies.
 *   - accessibility: decorative by default (`aria-hidden`) because the name
 *     beside the circle is the accessible identity; `label` opts into
 *     `role="img"` and doubles as the photo's `alt`. A decorative photo gets
 *     `alt=""`, never the initials — a screen reader would read "DR" twice.
 *
 * `initials` is a display string on purpose: the initials FUNCTION is the
 * caller's single source — `staffInitials` for a staff member, `bidderInitials`
 * for a customer or business name, bidder-web's `userInitials` for the signed-in
 * buyer (ADR 058 — Customer Identity; ADR 012 — Bidder Identity keeps the
 * customer resolver out of bidder-web, so the core never imports one). An empty
 * string keeps the fixed box: a nameless buyer's row must not shift left.
 */
'use client';

import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

/** Circle by default; the admin settings rails are squares. */
export type AvatarShape = 'circle' | 'square';

/**
 * Per-app appearance binding — class names only, no structure.
 *
 * Every slot is required: an avatar with no size or tone map has no box to
 * draw, so there is no meaningful bare default the way `Alert` has one.
 */
export interface AvatarDialect<Size extends string, Tone extends string> {
    /** The box — layout, centring, clipping, base type weight. */
    root: string;
    /** Box + type size per size. */
    sizes: Record<Size, string>;
    /** Fill / text / edge per tone. */
    tones: Record<Tone, string>;
    /** Applied for `shape="circle"`. */
    circle: string;
    /** Applied for `shape="square"`. */
    square: string;
    /** The photo — fills the box, cover-fitted. */
    image: string;
}

/** Props for the shared Avatar core. Skins re-export their own app-facing shape. */
export interface AvatarProps<Size extends string, Tone extends string> {
    /** The initials to draw, already derived by the caller's single initials source. */
    initials: string;
    /** A photo URL. When set, the photo replaces both the initials and the icon child. */
    src?: string | null;
    /** An icon to draw instead of the initials (no photo). Rendered verbatim. */
    children?: ReactNode;
    /** One of the skin's sizes. */
    size: Size;
    /** One of the skin's tones. */
    tone: Tone;
    /** Defaults to `circle`. */
    shape?: AvatarShape;
    /** When set, the avatar is an image with this accessible name instead of decorative. */
    label?: string;
    /** Native tooltip on the box — the clerk console's identity chip uses it. */
    title?: string;
    /** Extra classes on the box — the caller's spacing lives here. */
    className?: string;
    /** Inline style on the box — the sanctioned slot for a runtime tenant tint. */
    style?: CSSProperties;
    /** Emitted as `data-testid` on the box. */
    testId?: string;
    /** The app's appearance binding. */
    dialect: AvatarDialect<Size, Tone>;
}

/**
 * A fixed-size avatar box showing a photo, an icon, or initials — in that
 * order of precedence — with the appearance bound by the skin's dialect.
 *
 * @param props - {@link AvatarProps}
 * @returns One `<span data-avatar>`, decorative unless labelled.
 */
export function Avatar<Size extends string, Tone extends string>({
    initials,
    src,
    children,
    size,
    tone,
    shape = 'circle',
    label,
    title,
    className,
    style,
    testId,
    dialect,
}: AvatarProps<Size, Tone>) {
    return (
        <span
            data-avatar
            data-testid={testId}
            title={title}
            style={style}
            {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
            className={clsx(
                dialect.root,
                dialect.sizes[size],
                dialect.tones[tone],
                shape === 'square' ? dialect.square : dialect.circle,
                className,
            )}
        >
            {src ? <img src={src} alt={label ?? ''} className={dialect.image} /> : (children ?? initials)}
        </span>
    );
}
